import React, { useEffect, useMemo, useState } from 'react';

import {
    DefaultContentAcquisitionEntity,
    Territory
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import alertErrorTokens from '@amzn/meridian-tokens/base/icon/alert';
import alertWarningTokens from '@amzn/meridian-tokens/base/icon/alert';
import downloadTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import FileInput from '@amzn/meridian/file-input';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import {
    CellClassRules,
    CellValueChangedEvent,
    ColumnState,
    CsvExportParams,
    GridApi,
    ICellRendererParams
} from 'ag-grid-community';
import { cloneDeep, filter, includes, isEmpty, isEqual, isNil, uniq } from 'lodash';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { FilterInput } from 'src/components/FilterInput';
import { PlanConfigurator } from 'src/components/RateCardDetail/CoreRates/PlanConfigurator/PlanConfigurator';
import { RateCardCoreRateGridColumnName } from 'src/constants';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import { CORE_RATE_CSV_UPLOAD_MISMATCH_WITH_TEMPLATE, MISSING_REQUIRED_VALUES } from 'src/constants/errorMessages';
import { CORE_RATES_COMMON_COLUMNS, CORE_RATES_CSV_COLUMNS, CORE_RATES_GRID_COLUMNS } from 'src/constants/rateCards';
import { WRITER_ROLE_SET } from 'src/constants/role';
import { AppContext } from 'src/contexts/appContext';
import { useGridCopyPasteHandler } from 'src/hooks/useGridCopyPasteHandler';
import { IRateCardCoreRateGridRowData, IRateCardDetailStepProps } from 'src/types/IRateCard';
import { hasNonEmptyNumberValue } from 'src/util/RateCards';
import { getCurrentDate } from 'src/util/dateTimeUtil';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getTerritoryFlagRenderer } from 'src/util/grid/cellRenderer';
import { dropUndefined } from 'src/util/list-utils';
import { validateRequired } from 'src/util/validationsUtil';

import { generateGridErrorId } from '../../AddPrimeAMFRateCard/rateCardValidationReducer';
import './CoreRates.scss';

export const CoreRates = ({
    rateCardValues,
    setRateCardValues,
    stepTitle,
    isEditMode,
    setIsNextDisabled,
    augmentedMetadata,
    dataLoaded
}: IRateCardDetailStepProps) => {
    const {
        internalData: internalCoreRates,
        setInternalData: setInternalCoreRates,
        isPasting,
        onPasteStart,
        onPasteEnd,
        processDataFromClipboard
    } = useGridCopyPasteHandler(rateCardValues.coreRates);
    const [filterText, setFilterText] = useState('');
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const { metadata, userRole } = React.useContext(AppContext);

    const territories: Territory[] = Object.values(metadata.territoryMapping);
    const CAEs: DefaultContentAcquisitionEntity[] = augmentedMetadata?.CaeMapping
        ? Object.values(augmentedMetadata.CaeMapping)
        : [];
    const currencies: string[] = useMemo(
        () =>
            dropUndefined(
                Array.from(new Set(territories.flatMap((t) => [t.accrualCurrency, t.settlementCurrency, t.currency])))
            ),
        [territories]
    );
    const [errors, setErrors] = useState<Set<string>>(new Set());
    const [errorRowCount, setErrorRowCount] = useState<number>(0);
    const [warnings, setWarnings] = useState<Set<string>>(new Set());
    const [warningRowCount, setWarningRowCount] = useState<number>(0);
    const [visibleRateCols, setVisibleRateCols] = useState<string[]>([]);
    const [alertMsg, setAlertMsg] = useState<string>('');
    const rowLimit = 250;

    const [isReuploadCoreRateModalOpen, setIsReuploadCoreRateModalOpen] = useState(false);
    const [csvFileToUpload, setCsvFileToUpload] = useState<File[]>([]);

    // Sync internalCoreRates with parent rateCardValues when data loads
    useEffect(() => {
        if (
            dataLoaded &&
            rateCardValues.coreRates &&
            rateCardValues.coreRates.length > 0 &&
            internalCoreRates.length === 0
        ) {
            setInternalCoreRates(cloneDeep(rateCardValues.coreRates));
        }
    }, [dataLoaded, rateCardValues.coreRates]);

    // Sync internalCoreRates with parent rateCardValues and validate
    useEffect(() => {
        // Sync and validate after pasting operation is complete
        if (!isEditMode || visibleRateCols.length === 0 || isPasting) {
            return;
        }

        syncCoreRatesStateAndValidate();
    }, [internalCoreRates, visibleRateCols, isPasting]);

    // hide the columns where no row has corresponding data
    useEffect(() => {
        if (agGridApi && !isEmpty(internalCoreRates)) {
            const rateColumnVisibleSet = new Set<string>();

            for (const row of internalCoreRates) {
                for (const column of CORE_RATES_COMMON_COLUMNS) {
                    // @ts-ignore
                    if (row[column] !== undefined && row[column] !== '' && !rateColumnVisibleSet.has(column)) {
                        rateColumnVisibleSet.add(column);
                    }
                }
            }

            const visibleColIds = Array.from(rateColumnVisibleSet);

            // Only continue to update state if difference in cols found
            if (!isEqual(visibleColIds.sort(), visibleRateCols.sort())) {
                setVisibleRateCols(visibleColIds);

                CORE_RATES_COMMON_COLUMNS.forEach((colId) => {
                    agGridApi.setColumnVisible(colId, visibleColIds.includes(colId));
                });
            }
        }
    }, [agGridApi, internalCoreRates]);

    // Set error row count
    useEffect(() => {
        const count = getRowCount(Array.from(errors));
        setErrorRowCount(count);
    }, [errors]);

    // Set warning row count
    useEffect(() => {
        const count = getRowCount(Array.from(warnings));
        setWarningRowCount(count);
    }, [warnings]);

    // Disable "Next" button if errors exist
    useEffect(() => {
        if (!setIsNextDisabled) return;
        setIsNextDisabled(errorRowCount > 0);
    }, [errorRowCount]);

    // Limit row count to 250
    useEffect(() => {
        if (internalCoreRates?.length > rowLimit) {
            setAlertMsg(`Number of rows cannot exceed ${rowLimit}`);
            setInternalCoreRates((prevState) => prevState.slice(0, rowLimit));
        }
    }, [internalCoreRates]);

    const handleCoreRateUpload = (acceptedFiles: File[]) => {
        setCsvFileToUpload(acceptedFiles);
        if (isEmpty(internalCoreRates)) {
            onFileAttached(acceptedFiles);
        } else {
            setIsReuploadCoreRateModalOpen(true);
        }
    };

    // Handle .csv upload
    const onFileAttached = (acceptedFiles: File[]) => {
        const newFile = acceptedFiles[0];

        const fileReader = new FileReader();

        fileReader.readAsText(newFile);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fileReader.onload = (event: any) => {
            const csvData = event.target.result;

            const rowData = csvData.split(/\r\n|\n|\r/);

            if (!validateColumnHeaders(rowData[0])) return;

            const gridRowData: IRateCardCoreRateGridRowData[] = [];
            for (let i = 1; i < rowData.length; i++) {
                const rateRow = rowData[i].split(',');
                const coreRateRowData = buildOneCoreRateRowData(rateRow);
                validateCoreRateRowData(coreRateRowData);
                gridRowData.push(coreRateRowData);
            }

            setInternalCoreRates(gridRowData);
        };
    };

    const cellClassRules = useMemo<CellClassRules>(
        () => ({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            'error-cell': (params) => errors.has(generateGridErrorId(Number(params.node.id), params.colDef.field!)),
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            'warning-cell': (params) => warnings.has(generateGridErrorId(Number(params.node.id), params.colDef.field!))
        }),
        [errors, warnings]
    );

    const coreRateGridColumnsIncludingCheckbox = [RateCardCoreRateGridColumnName.CheckBox, ...CORE_RATES_GRID_COLUMNS];

    const columnDefs = coreRateGridColumnsIncludingCheckbox.map((colName) => {
        const baseDef = {
            field: colName,
            cellStyle: { borderRight: '1px solid #dde2eb' },
            cellClassRules,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            editable: isEditMode,
            valueFormatter: emptyCellFormatter,
            minWidth: 120
        };

        function useRichSelect(values: (string | undefined)[]) {
            return {
                ...baseDef,
                cellClass: 'core-rates-cell',
                cellClassRules,
                cellEditor: 'agRichSelectCellEditor',
                cellEditorPopup: true,
                cellEditorParams: { values: dropUndefined(values) }
            };
        }

        if (colName === RateCardCoreRateGridColumnName.Territory) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const territorySelectDef: any = useRichSelect(
                territories.filter((t) => t.territoryCode !== GLOBAL_TERRITORY.NAME).map((t) => t.territoryCode)
            );
            territorySelectDef.cellEditorParams.cellRenderer = (params: ICellRendererParams) =>
                getTerritoryFlagRenderer(params, false);
            territorySelectDef.cellRenderer = (params: ICellRendererParams) => getTerritoryFlagRenderer(params, false);
            return territorySelectDef;
        } else if (colName === RateCardCoreRateGridColumnName.CAE) {
            return useRichSelect(Array.from(new Set(CAEs.map((t) => t.contentAcquisitionEntityCode))));
        } else if (
            colName === RateCardCoreRateGridColumnName.RatecardCurrency ||
            colName === RateCardCoreRateGridColumnName.SettlementCurrency
        ) {
            return useRichSelect(currencies);
        } else if (colName === RateCardCoreRateGridColumnName.CheckBox) {
            return {
                headerName: '',
                field: colName,
                floatingFilter: false,
                hide: !isEditMode,
                checkboxSelection: true,
                headerCheckboxSelection: true,
                width: 50,
                sortable: false
            };
        } else {
            return baseDef;
        }
    });

    const rateGridSortModel: ColumnState[] = [
        { colId: RateCardCoreRateGridColumnName.Territory, sort: 'asc', sortIndex: 0 }
    ];

    const syncCoreRatesStateAndValidate = () => {
        setRateCardValues((prevState) => ({
            ...prevState,
            coreRates: internalCoreRates
        }));
        validateCoreRates();
    };

    const onCellValueChanged = (event: CellValueChangedEvent) => {
        // Ignore syncing and validating if pasting multiple cells at once
        if (isPasting) {
            return;
        }

        // Auto-populate Settlement Currency and CAE when Territory changed
        if (event.colDef.field === RateCardCoreRateGridColumnName.Territory) {
            setInternalCoreRates((prevState) => {
                const newState = [...prevState];
                const rowId = Number(event.node.id);
                populateSettlementCurrencyAndCae(newState[rowId]);
                return newState;
            });

            return;
        }

        syncCoreRatesStateAndValidate();
    };

    const populateSettlementCurrencyAndCae = (coreRate: IRateCardCoreRateGridRowData) => {
        const territory = territories.find((territory) => territory.territoryCode === coreRate.Territory);
        coreRate[RateCardCoreRateGridColumnName.SettlementCurrency] = territory?.settlementCurrency;
        coreRate[RateCardCoreRateGridColumnName.CAE] = CAEs.find(
            (CAE) => CAE.territoryID === territory?.territoryId && CAE.serviceID === 1
        )?.contentAcquisitionEntityCode;
    };

    const buildOneCoreRateRowData = (rateArr: string[]) => {
        return rateArr.reduce((acc: IRateCardCoreRateGridRowData, curr: string, index: number) => {
            acc[CORE_RATES_CSV_COLUMNS[index]] = curr;
            if (index === 0) {
                populateSettlementCurrencyAndCae(acc);
            }
            return acc;
        }, {} as IRateCardCoreRateGridRowData);
    };

    const validateCoreRateRowData = (rowData: IRateCardCoreRateGridRowData) => {
        const isValidTerritory = territories.some(
            (t) => t.territoryCode === rowData[RateCardCoreRateGridColumnName.Territory]
        );
        const isValidRatecardCurrency = currencies.some(
            (c) => c === rowData[RateCardCoreRateGridColumnName.RatecardCurrency]
        );

        if (!isValidTerritory) rowData[RateCardCoreRateGridColumnName.Territory] = '';
        if (!isValidRatecardCurrency) rowData[RateCardCoreRateGridColumnName.RatecardCurrency] = '';
    };

    const exportCoreRates = () => {
        const params: CsvExportParams = {
            fileName: `core_rates_export_${getCurrentDate()}.csv`
        };
        agGridApi?.exportDataAsCsv(params);
    };

    const validateCoreRates = () => {
        const newErrors = new Set<string>();
        const newWarnings = new Set<string>();
        const territoryInputs: string[] = [];

        internalCoreRates.forEach((coreRate, index) => {
            if (coreRate[RateCardCoreRateGridColumnName.Territory]) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                territoryInputs.push(coreRate[RateCardCoreRateGridColumnName.Territory]!);
            }

            validateOutlier(coreRate, index, newWarnings);

            CORE_RATES_GRID_COLUMNS.forEach((column) => {
                const cellValue = coreRate[column];
                const errorId = generateGridErrorId(index, column);

                if (CORE_RATES_COMMON_COLUMNS.includes(column)) {
                    if (!visibleRateCols.includes(column)) return;

                    if (
                        ![
                            RateCardCoreRateGridColumnName.BlendedPSMFloor,
                            RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM,
                            RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM,
                            RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP
                        ].includes(column) &&
                        (isNil(cellValue) || cellValue === '' || isNaN(Number(cellValue)))
                    ) {
                        newErrors.add(errorId);
                    } else if (hasNonEmptyNumberValue(cellValue) && Number(cellValue) <= 0) {
                        newWarnings.add(errorId);
                    }
                } else {
                    if (!validateRequired(cellValue?.toString())) {
                        newErrors.add(errorId);
                    }
                }
            });
        });

        validateDuplicateTerritories(territoryInputs, newErrors);

        setErrors(newErrors);
        setWarnings(newWarnings);
    };

    const validateDuplicateTerritories = (territoryInputs: string[], newErrors: Set<string>) => {
        const duplicateTerritories = uniq(
            filter(territoryInputs, (val, i, iteratee) => includes(iteratee, val, i + 1))
        );

        internalCoreRates.forEach((coreRate, index) => {
            if (!coreRate[RateCardCoreRateGridColumnName.Territory]) return;

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (duplicateTerritories.includes(coreRate[RateCardCoreRateGridColumnName.Territory]!)) {
                newErrors.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.Territory));
            }
        });
    };

    const validateColumnHeaders = (headers: string) => {
        const headerArr = headers.split(',');

        const mismatchHeaderExist = headerArr.some((header, index) => header !== CORE_RATES_CSV_COLUMNS[index]);

        if (mismatchHeaderExist || headerArr.length !== CORE_RATES_CSV_COLUMNS.length) {
            setAlertMsg(CORE_RATE_CSV_UPLOAD_MISMATCH_WITH_TEMPLATE);
            return false;
        }

        return true;
    };

    const validateOutlier = (coreRate: IRateCardCoreRateGridRowData, index: number, newWarnings: Set<string>) => {
        const {
            [RateCardCoreRateGridColumnName.IndMonthlyPSM]: individualRate,
            [RateCardCoreRateGridColumnName.HDIndMonthlyPSM]: individualHDRate,
            [RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM]: individualMontanaRate,
            [RateCardCoreRateGridColumnName.FamMonthlyPSM]: familyRate,
            [RateCardCoreRateGridColumnName.HDFamMonthlyPSM]: familyHDRate,
            [RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM]: familyMontanaRate,
            [RateCardCoreRateGridColumnName.NinetyDayFTCPP]: ninetyDayFTRate,
            [RateCardCoreRateGridColumnName.HDNinetyDayFTCPP]: ninetyDayFTHDRate,
            [RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP]: ninetyDayFTMontanaRate,
            [RateCardCoreRateGridColumnName.StudentMonthlyPSM]: studentRate
        } = coreRate;

        if (hasNonEmptyNumberValue(individualRate)) {
            // Validation: HD and non-HD rates should be the same
            if (hasNonEmptyNumberValue(individualHDRate) && Number(individualHDRate) !== Number(individualRate)) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.HDIndMonthlyPSM));
            }

            // Validation: Montana rates should match non-Montana rates
            if (
                hasNonEmptyNumberValue(individualMontanaRate) &&
                Number(individualMontanaRate) !== Number(individualRate)
            ) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.MontanaIndMonthlyPSM));
            }

            // Validation: Family rates should be larger than Individual rates
            if (hasNonEmptyNumberValue(familyRate) && Number(individualRate) >= Number(familyRate)) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.FamMonthlyPSM));
            }

            // Validation: Student rates should be less than Individual rates
            if (hasNonEmptyNumberValue(studentRate) && Number(individualRate) <= Number(studentRate)) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.StudentMonthlyPSM));
            }
        }

        if (hasNonEmptyNumberValue(familyRate)) {
            // Validation: HD and non-HD rates should be the same
            if (hasNonEmptyNumberValue(familyHDRate) && Number(familyHDRate) !== Number(familyRate)) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.HDFamMonthlyPSM));
            }

            // Validation: Montana rates should match non-Montana rates
            if (hasNonEmptyNumberValue(familyMontanaRate) && Number(familyMontanaRate) !== Number(familyRate)) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.MontanaFamMonthlyPSM));
            }
        }

        if (hasNonEmptyNumberValue(ninetyDayFTRate)) {
            // Validation: HD and non-HD rates should be the same
            if (hasNonEmptyNumberValue(ninetyDayFTHDRate) && Number(ninetyDayFTHDRate) !== Number(ninetyDayFTRate)) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.HDNinetyDayFTCPP));
            }

            // Validation: Montana rates should match non-Montana rates
            if (
                hasNonEmptyNumberValue(ninetyDayFTMontanaRate) &&
                Number(ninetyDayFTMontanaRate) !== Number(ninetyDayFTRate)
            ) {
                newWarnings.add(generateGridErrorId(index, RateCardCoreRateGridColumnName.MontanaNinetyDayFTCPP));
            }
        }
    };

    const getRowCount = (cells: string[]) => {
        const rows = new Set<string>();
        cells.forEach((cell) => {
            rows.add(cell.split('-*-')[0]);
        });
        return rows.size;
    };

    const handleAddRowOnClick = () => {
        const newRowData = cloneDeep(internalCoreRates);
        newRowData.push({} as IRateCardCoreRateGridRowData);
        setInternalCoreRates(newRowData);
    };

    const handleRemoveRowOnClick = () => {
        const selectedRows = agGridApi?.getSelectedNodes();
        if (selectedRows == undefined) return;
        // row id is maintained as unchanged after sorting or filtering
        const selectedRowsIdArr = selectedRows.map((row) => row.id);
        const newRowData = internalCoreRates.filter((row, index) => !selectedRowsIdArr.includes(index.toString()));
        setInternalCoreRates(newRowData);
    };

    const planConfigurator = (
        <PlanConfigurator
            isEditMode={isEditMode}
            planConfiguration={rateCardValues.planConfiguration}
            setPlanConfiguration={(newConfig) =>
                setRateCardValues((prev) => ({
                    ...prev,
                    planConfiguration: newConfig
                }))
            }
        />
    );

    const warningMsg = (
        <>
            <p>Expected rate relationships</p>
            <ul>
                <li>All rates should be &gt; 0</li>
                <li>Fam rate &gt; Ind rate</li>
                <li>Student rate &lt; Ind rate</li>
                <li>HD rate = non-HD rate</li>
                <li>Montana rate = non-Montana rate</li>
            </ul>
        </>
    );

    return (
        <Column className="core-rates-div" spacing="300" height="100%" width="100%" minHeight={480}>
            {isEditMode ? (
                <>
                    {alertMsg && (
                        <Row className="core-rates-error-alert">
                            <Alert type="error" onClose={() => setAlertMsg('')} width="100%">
                                {alertMsg}
                            </Alert>
                        </Row>
                    )}
                    <Heading level={2}>{stepTitle}</Heading>
                    <Row alignmentHorizontal="justify">
                        {isEditMode && (
                            <Row className="territory-overrides-button" spacing="400">
                                {planConfigurator}
                                <Button onClick={handleAddRowOnClick} type="tertiary" size="small">
                                    Add a row
                                </Button>
                                <Button onClick={handleRemoveRowOnClick} type="tertiary" size="small">
                                    Remove selected row(s)
                                </Button>
                                {errorRowCount > 0 && (
                                    <Tooltip position={'top'} title={MISSING_REQUIRED_VALUES}>
                                        <Box type="fill" spacingInset="200 300">
                                            <Row spacing="300">
                                                <Icon tokens={alertErrorTokens} />
                                                <Text type="h100" data-testid="error-row-count">
                                                    {errorRowCount}
                                                </Text>
                                                <Text>invalid</Text>
                                            </Row>
                                        </Box>
                                    </Tooltip>
                                )}
                                {warningRowCount > 0 && (
                                    <Tooltip position={'end'} title={warningMsg}>
                                        <Box type="fill" spacingInset="200 300">
                                            <Row spacing="300">
                                                <Icon tokens={alertWarningTokens} />
                                                <Text type="h100" data-testid="warning-row-count">
                                                    {warningRowCount}
                                                </Text>
                                                <Text>unexpected</Text>
                                            </Row>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Row>
                        )}
                        <Row>
                            {isPasting && <Loader size={'medium'} />}
                            <Button type="link" href="/CoreRatesTemplate.csv" download="CoreRatesTemplate.csv">
                                <Icon tokens={downloadTokens} />
                                Core rates template
                            </Button>
                            <FileInput
                                type="single"
                                uploadButtonLabel="Upload core rates"
                                uploadButtonType="link"
                                onFileAttached={handleCoreRateUpload}
                                data-testid="csv-input"
                                accept=".csv"
                            />
                        </Row>
                    </Row>
                </>
            ) : (
                <Box type="fill" spacingInset="400">
                    <Row alignmentHorizontal="justify">
                        <FilterInput filterText={filterText} setFilterText={setFilterText} />
                        <Row>
                            {planConfigurator}
                            <Button
                                type="link"
                                size="small"
                                onClick={exportCoreRates}
                                disabled={!WRITER_ROLE_SET.has(userRole)}
                            >
                                <Icon tokens={downloadTokens} />
                                Download
                            </Button>
                        </Row>
                    </Row>
                </Box>
            )}

            <div className="core-rates-grid">
                <BaseAgGrid
                    columnDefs={columnDefs}
                    rowData={internalCoreRates}
                    onCellValueChanged={onCellValueChanged}
                    processDataFromClipboard={processDataFromClipboard}
                    filterText={filterText}
                    setAgGridApi={setAgGridApi}
                    singleClickEdit={false}
                    onPasteStart={onPasteStart}
                    onPasteEnd={onPasteEnd}
                    suppressRowClickSelection={true}
                    enableRangeSelection={true}
                    sortModel={rateGridSortModel}
                    loading={!dataLoaded}
                />
            </div>

            <ConfirmationModal
                isOpen={isReuploadCoreRateModalOpen}
                onClose={() => setIsReuploadCoreRateModalOpen(false)}
                title="Confirm re-upload"
                body="Are you sure you want to re-upload rates? Any changes will be lost."
                showComments={false}
                primaryButtonText="Yes, upload"
                primaryButtonOnClick={() => {
                    setIsReuploadCoreRateModalOpen(false);
                    onFileAttached(csvFileToUpload);
                }}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsReuploadCoreRateModalOpen(false)}
            />
        </Column>
    );
};
